<template>
    <div class="wrapper">
        <section v-if="settings">
            <h2>Mobile messenger settings</h2>
            <p>These settings will only affect the android and iOS libraries</p>

            <section>
                <h4>Upload</h4>
                <div class="row">
                    <custom-input name="allowImage" label="Allow Image" type="toggle" v-model="settings.allowImage"/>
                    <help-popover>
                        <template slot="content">
                            <p>Defines if it should be possible for the user to send images</p>
                        </template>
                    </help-popover>
                </div>
            </section>

            <section>
                <h4>Visuals</h4>
                <div class="row">
                    <custom-input class="backgroundImage" name="backgroundImage" ref="backgroundImage" label="Background Image" type="text" v-model="settings.backgroundImage" placeholder="URL" :validationRules="{url: 'require_protocol'}"/>
                    <help-popover>
                        <template slot="content">
                            <p>An url where you can find the image that should be used as background, this replaces background color which should still be used as a fallback</p>
                        </template>
                    </help-popover>
                </div>

                <div class="row">
                    <custom-input name="balloonStyle"
                                  label="Balloon Style"
                                  type="dropdown"
                                  class="dropdown"
                                  :items="[
                          {value: '1', label: '1'},
                          {value: '2', label: '2'},
                          {value: '3', label: '3'}
                          ]"
                                  v-model="settings.balloonStyle"/>
                    <help-popover>
                        <template slot="content">
                            <p>Defines which style of chat balloon to use (user and account side)</p>
                        </template>
                    </help-popover>
                </div>

                <div class="row">
                    <custom-input name="font"
                                  label="Font Style"
                                  type="dropdown"
                                  class="dropdown"
                                  :items="fontValues"
                                  :disabled="fontDisabled"
                                  v-model="settings.font"/>
                    <help-popover>
                        <template slot="content">
                            <p>The font for all text</p>
                        </template>
                    </help-popover>
                </div>

                <div class="row">
                    <custom-input name="dateBorder" label="Date border" type="toggle" v-model="settings.dateBorder"/>
                    <help-popover>
                        <template slot="content">
                            <p>Defines if there should be a border around the date</p>
                        </template>
                    </help-popover>
                </div>

                <div class="row">
                    <custom-input name="navHideCloseButton" label="Nav Hide Close Button" type="toggle" v-model="settings.navHideCloseButton"/>
                    <help-popover>
                        <template slot="content">
                            <p>If the close/back button should be hidden</p>
                        </template>
                    </help-popover>
                </div>
            </section>

            <section>
                <h4>Info message</h4>
                <div class="row">
                    <custom-input class="infoText" name="infoText" label="Info Text" type="textarea" v-model="settings.infoText" placeholder="Info Text"/>
                    <help-popover>
                        <template slot="content">
                            <p>The welcome text that is shown when a user opens the messages view for the first time. This message wil always be on top of all the messages send between a user and account</p>
                            <br>
                            <small style="color:red">NOT YEST SUPPORTED IN WEB LIBRARY</small>
                        </template>
                    </help-popover>
                </div>

                <div class="row">
                    <custom-input class="infoText" name="infoText2" label="Info Text 2" type="textarea" v-model="settings.infoText2" placeholder="Info Text 2"/>
                    <help-popover>
                        <template slot="content">
                            <p>The welcome text that is shown when a user opens the messages view for the first time. But the user hasn't granted push notification rights. It displays instead of <code>infoText</code></p>
                            <br>
                            <small style="color:red">NOT YEST SUPPORTED IN WEB LIBRARY</small>
                        </template>
                    </help-popover>
                </div>

                <div class="row">
                    <custom-input name="infoTextBorder"
                                  label="Info Text Border"
                                  type="dropdown"
                                  class="dropdown"
                                  :items="[
                          {value: '0', label: 'No border'},
                          {value: '1', label: 'Top and bottom border'},
                          {value: '2', label: 'All borders'}
                          ]"
                                  v-model="settings.infoTextBorder"/>
                    <help-popover>
                        <template slot="content">
                            <p>Defines the type of border to show around the infoText element</p>
                        </template>
                    </help-popover>
                </div>
            </section>

            <button class="primary" v-on:click="validate">Save</button>
        </section>

        <p v-if="!settings && showLoading">
            Loading design settings...
        </p>
    </div>
</template>

<script>
import { Default } from '@/mixins/mixins';
import customInput from '@/components/additional/customInput';
import HelpPopover from '../additional/helpPopover';

export default {
    name: 'settingsMessenger',
    mixins: [
        Default
    ],
    inject: {
        $validator: '$validator'
    },
    props: ['libraryVersion'],
    components: {
        HelpPopover,
        customInput
    },
    data() {
        return {
            settings: null,
            showLoading: false,
            fontValues: [
                {value: '1', label: 'San Serif'},
                {value: '2', label: 'Serif'},
                {value: '3', label: 'Monospace'},
                {value: '4', label: 'Open sans'},
                {value: '5', label: 'PT sans'}
            ],
            fontDisabled: false
        };
    },
    created: function() {
        // Only show loading when it takes longer than X ms
        window.setTimeout(() => {
            this.showLoading = true;
        }, 500);

        this.$http.get('v1.1/design').then(response => {
            // TODO: (same code as in appearance.vue) Do this in parent comp instead of each child
            let settings = response.data.data;

            // Parse null settings
            Object.keys(settings).map((settingKey) => {
                if (settings[settingKey] === null)
                    settings[settingKey] = '';
            });

            // Font 99 is classified and specific to one customer
            if (settings.font === 99) {
                this.fontValues = [{value: '99', label: 'Custom'}];
                this.fontDisabled = true;
            }

            this.settings = settings;
        }, () => {
            // Error callback
        });
    },
    methods: {
        validate: function() {
            this.$parleyAdmin.validateMultiple([this.$refs.backgroundImage], function(value) {
                if (value)
                    return this.saveMessengerSettings();
                else {
                    this.$parleyAdmin.queryErrors.addError('Something is wrong with the data you provided. Please check all fields', this.$parleyAdmin.queryErrors.TYPE_ERROR);
                    return false;
                }
            }.bind(this));
        },
        saveMessengerSettings: function() {
            let _settings = {
                allowImage: this.settings.allowImage,
                backgroundImage: this.settings.backgroundImage,
                balloonStyle: this.settings.balloonStyle,
                dateBorder: this.settings.dateBorder,
                infoText: this.settings.infoText,
                infoText2: this.settings.infoText2,
                infoTextBorder: this.settings.infoTextBorder,
                navHideCloseButton: this.settings.navHideCloseButton
            };

            if (this.settings.font !== 99)
                _settings.font = this.settings.font;

            return this.$http.put('v1.1/design', _settings).then(() => {
                this.$parleyAdmin.queryErrors.addError('Settings saved', this.$parleyAdmin.queryErrors.TYPE_SUCCESS);
            }, () => {
                this.$parleyAdmin.queryErrors.addError('Error occurred when trying to save');
            });
        }
    }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
    p {
        margin-bottom: 1em;

    }

    section {
        margin-bottom: 1em;

        .custom-input {
            padding: 0 0 1em 0;

            &.infoText {
                width: 30em;
            }

            &.backgroundImage {
                width: 40em;
            }
        }

        .v-popover {
            .trigger {
                i {
                    margin-left: .25em;
                }
            }
        }

        h4 {
            padding: 0;
        }
    }

    button {
        margin: 1em 0 0 0;
    }
</style>
